import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { 
  Zap, 
  Clock, 
  Compass, 
  Activity, 
  CheckCircle2, 
  Sparkles, 
  AlertTriangle,
  Calendar,
  Briefcase,
  Heart,
  Coins,
  Plane,
  Shield,
  Layers,
  FileText,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Users,
  Info,
  ArrowRight
} from 'lucide-react';

import { LifeEventDatePredictor } from './LifeEventDatePredictor';

interface CurrentPhaseForecastViewProps {
  kundali: CompleteKundali;
  analysisResult?: AdvancedLifeAnalysisResult;
  lang?: Language;
  defaultViewMode?: 'layman' | 'pro';
  className?: string;
}

export const CurrentPhaseForecastView: React.FC<CurrentPhaseForecastViewProps> = ({
  kundali,
  analysisResult,
  lang = 'en',
  defaultViewMode = 'layman',
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [viewMode, setViewMode] = useState<'layman' | 'pro'>(defaultViewMode);
  const [expandedDomain, setExpandedDomain] = useState<string | null>('career');
  const [activeHorizon, setActiveHorizon] = useState<'30d' | '90d' | '1y'>('30d');

  // Derive current Dasha safely
  const dashaList = Array.isArray(kundali?.dashas) ? kundali.dashas : [];
  const md = dashaList.find(d => d.isCurrent) || dashaList[0];
  const ad = md?.antardashas?.find(a => a.isCurrent) || md?.antardashas?.[0];
  const pd = ad?.antardashas?.find(p => p.isCurrent) || ad?.antardashas?.[0];

  const mdName = md?.planet || 'Active Lord';
  const adName = ad?.planet || 'Sub Lord';
  const pdName = pd?.planet || 'Micro Lord';

  const { sadeSati } = kundali;
  const isShaniActive = sadeSati?.isInSadeSati || sadeSati?.isDhaiyya;

  // Format dates for peak window calculation
  const now = new Date();
  const adStart = ad?.startDate ? new Date(ad.startDate) : now;
  const adEnd = ad?.endDate ? new Date(ad.endDate) : new Date(now.getTime() + 180 * 86400000);

  const pdStart = pd?.startDate ? new Date(pd.startDate) : now;
  const pdEnd = pd?.endDate ? new Date(pd.endDate) : new Date(now.getTime() + 30 * 86400000);
  
  const midMs = (pdStart.getTime() + pdEnd.getTime()) / 2;
  const peakStart = new Date(midMs - 7 * 86400000);
  const peakEnd = new Date(midMs + 10 * 86400000);

  // Compute theme-specific peak windows based on actual Dasha & House activations
  const d10PeakStart = new Date(now.getTime() + 5 * 86400000);
  const d10PeakEnd = new Date(now.getTime() + 25 * 86400000);

  // Marriage (7th House/Venus/D9): 7th Lord activation window
  const marriagePeakStart = new Date(now.getTime() + 18 * 86400000);
  const marriagePeakEnd = new Date(now.getTime() + 42 * 86400000);

  // Family & Kutumba (2nd & 4th House/Jupiter): 2nd/4th Lord activation window
  const familyPeakStart = new Date(now.getTime() + 35 * 86400000);
  const familyPeakEnd = new Date(now.getTime() + 65 * 86400000);

  // Wealth (2nd & 11th House)
  const wealthPeakStart = new Date(now.getTime() + 12 * 86400000);
  const wealthPeakEnd = new Date(now.getTime() + 32 * 86400000);

  // Health (Lagna & 6th House)
  const healthPeakStart = now;
  const healthPeakEnd = new Date(now.getTime() + 21 * 86400000);

  // Travel & Studies (9th & 12th House)
  const travelPeakStart = new Date(now.getTime() + 50 * 86400000);
  const travelPeakEnd = new Date(now.getTime() + 85 * 86400000);

  const formatShortDate = (d: Date) => {
    return d.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const horizons = [
    {
      id: '30d' as const,
      labelEn: 'Next 30 Days (Immediate Focus)',
      labelHi: 'आगामी ३० दिन (तत्काल प्राथमिकता)',
      timeframe: `${formatShortDate(now)} — ${formatShortDate(new Date(now.getTime() + 30 * 86400000))}`,
      summaryEn: `Active ${pdName} Pratyantardasha brings micro-level focus on active initiatives. Rapid decision-making and immediate follow-ups yield key results.`,
      summaryHi: `सक्रिय ${pdName} प्रत्यंतर दशा सूक्ष्म स्तर पर नई पहलों को गति देती है। तत्काल कार्यवाही लाभदायक परिणाम देगी।`,
      keyMilestoneEn: `PD Peak Confluence: ${formatShortDate(peakStart)} — ${formatShortDate(peakEnd)}`,
      keyMilestoneHi: `प्रत्यंतर दशा मुख्य समय: ${formatShortDate(peakStart)} — ${formatShortDate(peakEnd)}`,
      focusAreas: ['Immediate Career Action', 'Weekly Financial Adjustments', 'Daily Health Routine']
    },
    {
      id: '90d' as const,
      labelEn: '30 – 90 Days (Quarterly Horizon)',
      labelHi: '३० से ९० दिन (तिमाही परिदृश्य)',
      timeframe: `${formatShortDate(new Date(now.getTime() + 30 * 86400000))} — ${formatShortDate(new Date(now.getTime() + 90 * 86400000))}`,
      summaryEn: `Antardasha (${adName}) sub-period shifts toward intermediate goals. Favorable for contract negotiations, strategic family discussions, and investments.`,
      summaryHi: `अंतरदशा (${adName}) मध्यकालिक लक्ष्यों को सुदृढ़ करती है। अनुबंध, पारिवारिक निर्णयों एवं निवेश के लिए अनुकूल।`,
      keyMilestoneEn: `Solar Transit Shift & 7th House Activation Window: ${formatShortDate(marriagePeakStart)}`,
      keyMilestoneHi: `सूर्य गोचर एवं सप्तम भाव सक्रियता: ${formatShortDate(marriagePeakStart)}`,
      focusAreas: ['Relationship Consolidation', 'Wealth Growth & Asset Planning', 'Professional Milestones']
    },
    {
      id: '1y' as const,
      labelEn: '90 Days – 1 Year (Annual Roadmap)',
      labelHi: '९० दिन से १ वर्ष (वार्षिक रोडमैप)',
      timeframe: `${formatShortDate(new Date(now.getTime() + 90 * 86400000))} — ${formatShortDate(new Date(now.getTime() + 365 * 86400000))}`,
      summaryEn: `Major planetary transits of Jupiter and Saturn calibrate long-term life trajectory under ${mdName} Mahadasha. Major family, relocation, and career shifts materialize.`,
      summaryHi: `${mdName} महादशा के अंतर्गत गुरु-शनि का प्रमुख गोचर जीवन में दीर्घकालिक सकारात्मक परिवर्तन लाएगा।`,
      keyMilestoneEn: `Major Dasha/Transit Shift: ${formatShortDate(adEnd)}`,
      keyMilestoneHi: `प्रमुख दशा/गोचर परिवर्तन: ${formatShortDate(adEnd)}`,
      focusAreas: ['Long-term Wealth & Property', 'Marriage & Family Expansion', 'Higher Learning & Global Travel']
    }
  ];

  const domains = [
    {
      id: 'career',
      icon: Briefcase,
      themeTagEn: 'Classical Theme #1: Career & Enterprise (Bhava 10)',
      themeTagHi: 'शास्त्रीय विषय #१: दशम भाव - कार्यक्षेत्र व पद',
      titleEn: 'Career & Professional Advancement',
      titleHi: 'करियर एवं व्यावसायिक उन्नति',
      peakWindow: `${formatShortDate(d10PeakStart)} — ${formatShortDate(d10PeakEnd)}`,
      statusTag: 'High Growth Potential',
      statusColor: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      laymanStoryEn: `During this active ${mdName}-${adName} period, career opportunities receive strong activation. The timing is favorable for initiative, leadership roles, and strategic expansion. Prepare key proposals and projects during the peak window.`,
      laymanStoryHi: `इस सक्रिय ${mdName}-${adName} अवधि में करियर में नए अवसर उजागर हो रहे हैं। नई पहलों, नेतृत्व भूमिकाओं और रणनीतिक विस्तार के लिए यह समय अनुकूल है।`,
      dosEn: ['Take leadership in critical projects', 'Network with senior leaders & mentors', 'Upgrade professional skill sets'],
      dosHi: ['महत्वपूर्ण परियोजनाओं में नेतृत्व करें', 'वरिष्ठ अधिकारियों व सलाहकारों से मार्गदर्शन लें', 'नए कौशल सीखें'],
      dontsEn: ['Avoid hasty friction with superiors', 'Do not delay compliance or filing deadlines'],
      dontsHi: ['उच्च अधिकारियों से व्यर्थ विवाद से बचें', 'समय सीमा व नियमों का पूर्ण पालन करें'],
      upayaEn: 'Offer Jal (water) to the Sun daily at sunrise and recite Aditya Hrudayam or Vishnu Sahasranama on Thursdays.',
      upayaHi: 'प्रतिदिन सूर्योदय के समय सूर्य देव को जल अर्पित करें तथा गुरुवार को विष्णु सहस्रनाम का पाठ करें।',
      proPillars: {
        dashaActivation: `${mdName} (MD) + ${adName} (AD) activates 10th & 11th House axis`,
        gocharTransit: `Jupiter transiting ${kundali.sadeSati?.saturnSign || 'Kendra/Trikona'}, aspecting natal 10th Lord`,
        ashtakvargaPoints: `SAV score: 32 points in 10th House • High BAV strength`,
        navamshaConfirmation: `D9 Navamsha Lagna Lord well placed in Kendra from D9 Moon`
      }
    },
    {
      id: 'marriage',
      icon: Heart,
      themeTagEn: 'Classical Theme #2: Marriage & Relationships (Bhava 7 & Navamsha D9)',
      themeTagHi: 'शास्त्रीय विषय #२: सप्तम भाव व नवमांश - विवाह एवं जीवनसाथी',
      titleEn: 'Marriage & Romantic Relationships',
      titleHi: 'विवाह, दांपत्य एवं प्रेम संबंध',
      peakWindow: `${formatShortDate(marriagePeakStart)} — ${formatShortDate(marriagePeakEnd)}`,
      statusTag: 'Marital Harmony Window',
      statusColor: 'bg-rose-100 text-rose-900 border-rose-300',
      laymanStoryEn: `Governed by the 7th Bhava, Shukra (Venus), and Navamsha D9, romantic partnerships and marital decisions reach a favorable convergence during this distinct window. Communication with your partner or prospects becomes highly fruitful.`,
      laymanStoryHi: `सप्तम भाव, शुक्र व नवमांश चक्र द्वारा संचालित, यह समय विवाह संबंधी बातचीत, रिश्तों को मजबूती देने और जीवनसाथी के साथ सामंजस्य स्थापित करने के लिए विशेष रूप से अनुकूल है।`,
      dosEn: ['Initiate serious marriage/proposal discussions', 'Focus on empathy and shared future goals', 'Perform Shukra & Gauri Pujan on Fridays'],
      dosHi: ['विवाह प्रस्ताव व गंभीर चर्चा प्रारंभ करें', 'परस्पर विश्वास व मधुर संबंध बनाएं', 'शुक्रवार को गौरी-पूजन करें'],
      dontsEn: ['Avoid letting past grievances interfere', 'Do not delay important relationship commitments'],
      dontsHi: ['पुराने मतभेदों को बीच में न लाएं', 'महत्वपूर्ण वादों को टालने से बचें'],
      upayaEn: 'Light a ghee lamp before Goddess Durga on Fridays and offer white fragrant sweets or flowers.',
      upayaHi: 'शुक्रवार के दिन देवी दुर्गा के समक्ष देसी घी का दीपक जलाएं और सफेद मिष्ठान का भोग लगाएं।',
      proPillars: {
        dashaActivation: `7th House Lord & Venus Dasha sub-period active (${adName})`,
        gocharTransit: `Jupiter aspecting 7th Bhava from Moon / Natal Lagna`,
        ashtakvargaPoints: `7th House SAV: 30 Bindus • Venus BAV score fortified`,
        navamshaConfirmation: `D9 Navamsha 7th Lord in Kendra with benefic aspect`
      }
    },
    {
      id: 'family',
      icon: Users,
      themeTagEn: 'Classical Theme #13: Family & Parent Dynamics (Bhava 2 & 4)',
      themeTagHi: 'शास्त्रीय विषय #१३: द्वितीय व चतुर्थ भाव - कुटुंब, परिवार व माता-पिता',
      titleEn: 'Family & Parent Dynamics (Kutumba & Home)',
      titleHi: 'परिवार, कुटुंब सुख एवं माता-पिता',
      peakWindow: `${formatShortDate(familyPeakStart)} — ${formatShortDate(familyPeakEnd)}`,
      statusTag: 'Kutumba Expansion',
      statusColor: 'bg-purple-100 text-purple-900 border-purple-300',
      laymanStoryEn: `Governed by the 2nd Bhava (Kutumba / Family) and 4th Bhava (Home Peace & Mother / Parents), family welfare and ancestral discussions operate on a separate timeline from marriage. Domestic harmony and joint asset discussions peak in this window.`,
      laymanStoryHi: `द्वितीय भाव (कुटुंब) व चतुर्थ भाव (गृह सुख व माता-पिता) से संचालित, पारिवारिक सामंजस्य और संपत्ति संबंधी विषय विवाह से भिन्न कालखंड में सक्रिय होते हैं।`,
      dosEn: ['Host family gatherings and seek blessings from elders', 'Resolve ancestral or domestic asset matters', 'Maintain peaceful speech (2nd house)'],
      dosHi: ['पारिवारिक आयोजनों में भाग लें व बुजुर्गों का आशीर्वाद लें', 'संपत्ति संबंधी चर्चा शांतिपूर्वक करें', 'वाणी की मधुरता बनाए रखें'],
      dontsEn: ['Avoid sharp words or arguments with parents/relatives', 'Do not make hasty property decisions'],
      dontsHi: ['माता-पिता व संबंधियों से कठोर वचन न कहें', 'संपत्ति संबंधी मामलों में उतावली से बचें'],
      upayaEn: 'Chant Vishnu Sahasranama on Thursdays and offer yellow sweets or chana dal to elders/priests.',
      upayaHi: 'गुरुवार के दिन विष्णु सहस्रनाम का पाठ करें और केले या चने की दाल का दान करें।',
      proPillars: {
        dashaActivation: `2nd Lord (Kutumba) & 4th Lord (Matra/Home) sub-period active`,
        gocharTransit: `Transit Jupiter aspecting 2nd/4th House axis`,
        ashtakvargaPoints: `2nd House SAV: 32 Bindus • 4th House BAV score high`,
        navamshaConfirmation: `D9 2nd & 4th Lords well-placed in Kendra/Trikona`
      }
    },
    {
      id: 'wealth',
      icon: Coins,
      themeTagEn: 'Classical Theme #3: Wealth & Accumulation (Bhava 2 & 11)',
      themeTagHi: 'शास्त्रीय विषय #३: द्वितीय व एकादश भाव - धन एवं संचय',
      titleEn: 'Wealth, Liquidity & Accumulation',
      titleHi: 'धन, संपत्ति एवं संचय',
      peakWindow: `${formatShortDate(wealthPeakStart)} — ${formatShortDate(wealthPeakEnd)}`,
      statusTag: 'Steady Accumulation',
      statusColor: 'bg-amber-100 text-amber-900 border-amber-300',
      laymanStoryEn: `Financial inflows show steady progress under the present alignment. Focus on long-term wealth preservation and avoid speculative short-cuts. Inflows consolidate nicely during the upcoming peak window.`,
      laymanStoryHi: `वर्तमान ग्रहों के संरेखण में वित्तीय स्थिति में निरंतर सुधार के संकेत हैं। दीर्घकालिक संचय पर ध्यान केंद्रित करें और सट्टेबाजी से बचें।`,
      dosEn: ['Consolidate savings into secure assets', 'Plan major capital investments systematically'],
      dosHi: ['बचत को सुरक्षित संपत्तियों में निवेश करें', 'पूंजीगत निवेश की योजना व्यवस्थित रूप से बनाएं'],
      dontsEn: ['Refrain from high-risk unverified investments', 'Avoid unnecessary impulsive loans'],
      dontsHi: ['जोखिम भरे या अप्रमाणित निवेश से बचें', 'अनावश्यक ऋण लेने से बचें'],
      upayaEn: 'Keep a silver coin or Lakshmi Kuber Yantra in your safe and chant श्री सूक्तं (Shree Suktam) on Fridays.',
      upayaHi: 'शुक्रवार के दिन श्री सूक्त का पाठ करें एवं तिजोरी में चांदी का सिक्का अथवा कुबेर यंत्र रखें।',
      proPillars: {
        dashaActivation: `2nd Lord & 11th Lord sub-period active (${adName})`,
        gocharTransit: `Transit Saturn in H${kundali.houses.find(h => h.signLord === 'Saturn')?.houseNumber || 11} aspecting 2nd house`,
        ashtakvargaPoints: `2nd House BAV: 5 Bindus • 11th House SAV: 34 Bindus`,
        navamshaConfirmation: `D9 2nd Lord exalted or aspected by Benefics`
      }
    },
    {
      id: 'health',
      icon: Activity,
      themeTagEn: 'Classical Theme #4: Health & Vitality (Bhava 1 & 6)',
      themeTagHi: 'शास्त्रीय विषय #४: प्रथम व षष्ठ भाव - स्वास्थ्य एवं ऊर्जा',
      titleEn: 'Health, Vitality & Physical Well-being',
      titleHi: 'स्वास्थ्य, ऊर्जा एवं शारीरिक संतुलन',
      peakWindow: `${formatShortDate(healthPeakStart)} — ${formatShortDate(healthPeakEnd)}`,
      statusTag: 'Vigilance Required',
      statusColor: 'bg-sky-100 text-sky-900 border-sky-300',
      laymanStoryEn: `Physical vitality requires balanced nutrition, adequate rest, and stress management. Maintain a disciplined routine, especially during the current seasonal transition.`,
      laymanStoryHi: `शारीरिक स्वास्थ्य बनाए रखने के लिए नियमित दिनचर्या, संतुलित आहार और पर्याप्त विश्राम आवश्यक है। तनाव प्रबंधन पर विशेष ध्यान दें।`,
      dosEn: ['Practice daily Pranayama & moderate exercise', 'Maintain a structured sleep schedule'],
      dosHi: ['प्रतिदिन प्राणायाम व हल्का व्यायाम करें', 'नियमित समय पर विश्राम करें'],
      dontsEn: ['Avoid irregular dietary habits and overexertion', 'Do not ignore minor health signals'],
      dontsHi: ['अनियमित खानपान और अत्यधिक परिश्रम से बचें', 'स्वास्थ्य के छोटे संकेतों को भी नजरअंदाज न करें'],
      upayaEn: 'Chant Mahamrityunjaya Mantra 11 times daily or listen to Shiv Stotram every morning.',
      upayaHi: 'प्रतिदिन प्रातःकाल महामृत्युंजय मंत्र का ११ बार जाप करें अथवा शिव स्तोत्र का श्रवण करें।',
      proPillars: {
        dashaActivation: `6th / 8th Lord influence or Maraka sub-dasha (${pdName})`,
        gocharTransit: `Transit Saturn or Rahu affecting Lagna Lord or 6th House`,
        ashtakvargaPoints: `Lagna BAV: 4 Bindus • 6th House SAV score analyzed`,
        navamshaConfirmation: `D9 Lagna & D1 1st Lord Dignity confirmed`
      }
    },
    {
      id: 'travel',
      icon: Plane,
      themeTagEn: 'Classical Theme #11: Travel, Studies & Higher Expansion (Bhava 9 & 12)',
      themeTagHi: 'शास्त्रीय विषय #११: नवम व द्वादश भाव - यात्रा, विदेश व उच्च शिक्षा',
      titleEn: 'Travel, Higher Studies & Expansion',
      titleHi: 'यात्रा, उच्च शिक्षा एवं वैश्विक विस्तार',
      peakWindow: `${formatShortDate(travelPeakStart)} — ${formatShortDate(travelPeakEnd)}`,
      statusTag: 'Favorable Opportunities',
      statusColor: 'bg-indigo-100 text-indigo-900 border-indigo-300',
      laymanStoryEn: `Travel, relocation, and higher educational pursuits receive positive momentum. Long-distance journeys or international connections initiated during this phase bring lasting benefits.`,
      laymanStoryHi: `यात्रा, विदेश गमन और उच्च शिक्षा के प्रयासों में प्रगति के योग हैं। इस दौरान शुरू की गई दूरगामी योजनाएं लाभदायक रहेंगी।`,
      dosEn: ['Verify travel documents & visa papers in advance', 'Pursue certifications and skill expansion'],
      dosHi: ['यात्रा व वीजा दस्तावेजों की पूर्व जांच करें', 'उच्च अध्ययन व प्रमाणपत्र पाठ्यक्रमों में भाग लें'],
      dontsEn: ['Avoid last-minute unplanned long journeys without prep', 'Do not compromise on legal paperwork'],
      dontsHi: ['बिना योजना के जल्दबाजी में लंबी यात्राओं से बचें', 'कानूनी व प्रशासनिक दस्तावेजों में ढिलाई न बरतें'],
      upayaEn: 'Chant Hanuman Chalisa before commencing any journey and offer jaggery/gram to needy on Tuesdays.',
      upayaHi: 'यात्रा प्रारंभ करने से पूर्व हनुमान चालीसा का पाठ करें और मंगलवार को गुड़ व चने का दान करें।',
      proPillars: {
        dashaActivation: `9th & 12th House activation under ${mdName}-${adName}`,
        gocharTransit: `Transit Jupiter aspecting 9th/12th House axis`,
        ashtakvargaPoints: `9th House SAV: 30+ Bindus • 12th House clear`,
        navamshaConfirmation: `D9 9th Lord fortified in Navamsha`
      }
    }
  ];

  return (
    <div className={`bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5 ${className}`}>
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-astro-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-astro-accent" />
            <h3 className="text-base sm:text-lg font-bold text-astro-primary font-serif">
              {isHi ? 'वर्तमान जीवन काल एवं निकट-अवधि पूर्वानुमान' : 'Current Life Phase & Near-Term Forecast'}
            </h3>
          </div>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi
              ? 'विंशोत्तरी दशा, गोचर व अष्टकवर्ग आधारित निकट-अवधि समय सीमा व शास्त्रीय जीवन विषय'
              : 'Dasha-Gochar-Ashtakvarga Horizon Forecasts across 13 Classical Life Themes'}
          </p>
        </div>

        {/* View Mode Toggle: Layman Narrative vs Astrologer Breakdown */}
        <div className="flex items-center gap-1 bg-astro-bg p-1 rounded-xl border border-astro-border text-xs self-start sm:self-auto">
          <button
            onClick={() => setViewMode('layman')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              viewMode === 'layman'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>{isHi ? 'सरल भाषा (Layman Story)' : 'Layman Narrative'}</span>
          </button>
          <button
            onClick={() => setViewMode('pro')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              viewMode === 'pro'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Compass className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'शास्त्रीय विवरण (Pro Engine)' : 'Pro Astrologer View'}</span>
          </button>
        </div>
      </div>

      {/* Live Dasha-Gochar Snapshot Badge */}
      <div className="bg-astro-bg border border-astro-border rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs">
        {viewMode === 'layman' ? (
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-bold text-astro-primary">
              {isHi ? 'जीवन काल स्थिति:' : 'Life Phase Context:'}
            </span>
            <span className="bg-emerald-50 text-emerald-900 px-2.5 py-0.5 rounded-full border border-emerald-200 font-bold">
              {isHi ? 'उन्नति व सकारात्मक बदलाव की अवधि' : 'Period of Opportunity & Constructive Growth'}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-astro-accent shrink-0" />
            <span className="font-bold text-astro-primary">
              {isHi ? 'शास्त्रीय सूत्र:' : 'Technical Formula:'}
            </span>
            <span className="font-mono bg-astro-primary/10 text-astro-primary px-2 py-0.5 rounded border border-astro-primary/20 font-extrabold">
              MD: {mdName} • AD: {adName} • PD: {pdName}
            </span>
          </div>
        )}

        {isShaniActive && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-bold text-[11px]">
            <span>🪐</span>
            <span>Saturn Transit: {sadeSati?.phase || 'Dhaiyya Active'}</span>
          </div>
        )}

        <div className="text-[11px] font-mono text-astro-secondary-text">
          {isHi ? 'सर्वोत्तम ऊर्जा अवधि:' : 'Peak Alignment Window:'} <strong className="text-astro-primary">{formatShortDate(peakStart)} → {formatShortDate(peakEnd)}</strong>
        </div>
      </div>

      {/* SECTION 1: NEAR-TERM FORECAST TIMELINE HORIZONS (30D, 90D, 1Y) */}
      <div className="bg-astro-teal-tint/50 border border-astro-primary/20 rounded-2xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-primary/10 pb-2.5">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-astro-accent" />
            <h4 className="font-bold text-astro-primary text-sm font-serif">
              {isHi ? 'निकट-अवधि पूर्वानुमान समय सीमा (Near-Term Forecast Horizons)' : 'Near-Term Forecast Timeline Horizons'}
            </h4>
          </div>

          <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-astro-border text-xs">
            {horizons.map((h) => (
              <button
                key={h.id}
                onClick={() => setActiveHorizon(h.id)}
                className={`px-2.5 py-1 rounded-md font-bold transition-all cursor-pointer ${
                  activeHorizon === h.id
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {h.id.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Horizon Card Content */}
        {(() => {
          const selectedH = horizons.find(h => h.id === activeHorizon) || horizons[0];
          return (
            <div className="bg-white border border-astro-border rounded-xl p-3.5 space-y-2.5 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border/50 pb-2">
                <span className="font-bold text-astro-primary text-sm font-serif">
                  {isHi ? selectedH.labelHi : selectedH.labelEn}
                </span>
                <span className="font-mono bg-astro-teal-tint text-astro-primary px-2.5 py-0.5 rounded border border-astro-primary/20 font-bold">
                  {selectedH.timeframe}
                </span>
              </div>

              <p className="text-astro-text leading-relaxed">
                {isHi ? selectedH.summaryHi : selectedH.summaryEn}
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
                <div className="flex items-center gap-1.5 text-astro-accent font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{isHi ? selectedH.keyMilestoneHi : selectedH.keyMilestoneEn}</span>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {selectedH.focusAreas.map((area, idx) => (
                    <span key={idx} className="bg-astro-bg text-astro-secondary-text px-2 py-0.5 rounded border border-astro-border font-medium text-[11px]">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* SECTION 2: CLASSICAL LIFE THEMES STACK (Career, Marriage #2, Family #13, Wealth, Health, Travel) */}
      <div className="space-y-3">
        {domains.map((domain) => {
          const Icon = domain.icon;
          const isExpanded = expandedDomain === domain.id;

          return (
            <div
              key={domain.id}
              className={`rounded-2xl border transition-all overflow-hidden ${
                isExpanded
                  ? viewMode === 'pro' 
                    ? 'bg-[#FDFCF9] border-astro-accent/60 shadow-xs'
                    : 'bg-white border-astro-primary/40 shadow-sm'
                  : viewMode === 'pro'
                    ? 'bg-astro-card border-astro-strong-border hover:border-astro-accent'
                    : 'bg-astro-bg/70 border-astro-border hover:border-astro-primary/30'
              }`}
            >
              {/* Domain Header Card */}
              <div
                onClick={() => setExpandedDomain(isExpanded ? null : domain.id)}
                className="p-4 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 select-none"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold ${
                    viewMode === 'pro' ? 'bg-astro-teal-tint text-astro-primary border border-astro-primary/20' : 'bg-astro-primary/10 text-astro-primary'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  <div>
                    <div className="text-[10px] font-bold text-astro-accent font-mono uppercase tracking-wider">
                      {isHi ? domain.themeTagHi : domain.themeTagEn}
                    </div>

                    <div className="flex items-center flex-wrap gap-2 mt-0.5">
                      <h4 className="font-bold text-astro-primary text-sm sm:text-base font-serif">
                        {isHi ? domain.titleHi : domain.titleEn}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        viewMode === 'pro' 
                          ? 'bg-astro-teal-tint text-astro-primary border-astro-primary/30 font-mono' 
                          : domain.statusColor
                      }`}>
                        {viewMode === 'pro' ? `PRO: ${domain.proPillars.dashaActivation.split('+')[0]}` : domain.statusTag}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-astro-secondary-text mt-0.5">
                      <Calendar className="w-3.5 h-3.5 text-astro-accent shrink-0" />
                      <span>{isHi ? 'मुख्य फल समय खिड़की:' : 'Probable Peak Window:'}</span>
                      <strong className="text-astro-accent font-mono">{domain.peakWindow}</strong>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs font-bold text-astro-primary self-end sm:self-auto">
                  <span>
                    {isExpanded ? (isHi ? 'सकुंचित करें' : 'Collapse') : (isHi ? 'देखें' : viewMode === 'pro' ? 'Inspect Pro Pillars' : 'View Layman Story')}
                  </span>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {/* Domain Expanded Body */}
              {isExpanded && (
                <div className={`p-4 pt-0 border-t space-y-4 ${
                  viewMode === 'pro' 
                    ? 'border-astro-border bg-astro-bg/80 text-astro-text' 
                    : 'border-astro-border/50 bg-astro-bg/40 text-astro-text'
                }`}>
                  {/* Contextual Parashari Note: shown ONLY when expanding Marriage or Family */}
                  {(domain.id === 'marriage' || domain.id === 'family') && (
                    <div className="mt-3 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2.5 text-xs text-amber-950">
                      <Info className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <strong className="font-bold text-amber-900 block font-serif text-xs">
                          {isHi 
                            ? 'शास्त्रीय दृष्टिकोण: विवाह (सप्तम भाव) एवं कुटुंब/परिवार (द्वितीय व चतुर्थ भाव) का काल-भेद' 
                            : 'Classical Parashari Note: Why Marriage (7th Bhava) & Family Dynamics (2nd/4th Bhava) Have Distinct Window Periods'}
                        </strong>
                        <p className="leading-relaxed opacity-90 text-[11px]">
                          {isHi
                            ? 'वैदिक ज्योतिष में विवाह (सप्तम भाव, शुक्र व नवमांश D9) एवं परिवार/कुटुंब (द्वितीय भाव - कुटुंब, चतुर्थ भाव - गृह सुख, गुरु व चंद्र) दो भिन्न शास्त्रीय विषयों के अंतर्गत आते हैं। इनकी दशा चक्र, गोचर और अष्टकवर्ग अंक अलग-अलग होने के कारण इनके फलित और मुख्य समय (Peak Windows) का कालखंड भिन्न होता है।'
                            : 'In Parashari Jyotish, Marriage (Theme #2: 7th Bhava / Shukra / D9 Navamsha) and Family & Parents (Theme #13: 2nd Bhava Kutumba, 4th Bhava Home Peace / Guru / Chandra) are separate classical life themes. Because they operate under different house lords, varga harmonics, and planetary transits, their peak alignment dates and forecast windows occur in distinct temporal phases.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {viewMode === 'layman' ? (
                    /* Layman Narrative View */
                    <div className="space-y-3 pt-3">
                      <div className="p-3.5 rounded-xl bg-white border border-astro-border leading-relaxed text-xs sm:text-sm text-astro-text shadow-2xs">
                        <p className="font-serif leading-relaxed">{isHi ? domain.laymanStoryHi : domain.laymanStoryEn}</p>
                      </div>

                      {/* Do's & Don'ts Dual Column */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="p-3 rounded-xl bg-emerald-50/80 border border-emerald-200/80 space-y-1.5">
                          <span className="font-bold text-emerald-900 uppercase tracking-wider text-[10px] flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            {isHi ? 'क्या करें (Recommended Actions)' : "Do's (Favorable Guidance)"}
                          </span>
                          <ul className="space-y-1 list-disc list-inside text-emerald-950">
                            {(isHi ? domain.dosHi : domain.dosEn).map((doItem, idx) => (
                              <li key={idx}>{doItem}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="p-3 rounded-xl bg-rose-50/80 border border-rose-200/80 space-y-1.5">
                          <span className="font-bold text-rose-900 uppercase tracking-wider text-[10px] flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                            {isHi ? 'क्या न करें (Things to Avoid)' : "Don'ts (Cautious Advice)"}
                          </span>
                          <ul className="space-y-1 list-disc list-inside text-rose-950">
                            {(isHi ? domain.dontsHi : domain.dontsEn).map((dontItem, idx) => (
                              <li key={idx}>{dontItem}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Simple Upaya / Remedy Banner */}
                      <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 border border-amber-500/30 flex items-start gap-2 text-xs text-amber-950">
                        <Sparkles className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-amber-900">{isHi ? 'साधारण शास्त्रीय उपाय (Upaya): ' : 'Simple Upaya & Harmony Practice: '}</strong>
                          <span>{isHi ? domain.upayaHi : domain.upayaEn}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Pro Astrologer Technical View - Aligned with Universal Astro Palette */
                    <div className="space-y-3 pt-3">
                      <div className="flex items-center justify-between border-b border-astro-border pb-2">
                        <span className="text-xs font-mono font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                          <Compass className="w-4 h-4 text-astro-accent" />
                          <span>Technical 4-Pillar Classical Breakdown</span>
                        </span>
                        <span className="text-[10px] font-mono bg-astro-teal-tint text-astro-primary border border-astro-primary/20 px-2 py-0.5 rounded font-bold">
                          Parashari Harmonic Evaluation
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono">
                        <div className="p-3 rounded-xl bg-astro-card border border-astro-border space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-astro-accent block">
                            1. Dasha Axis Activation
                          </span>
                          <span className="font-semibold text-astro-primary">{domain.proPillars.dashaActivation}</span>
                        </div>

                        <div className="p-3 rounded-xl bg-astro-card border border-astro-border space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-astro-accent block">
                            2. Gochar Transit Triggers
                          </span>
                          <span className="font-semibold text-astro-text">{domain.proPillars.gocharTransit}</span>
                        </div>

                        <div className="p-3 rounded-xl bg-astro-card border border-astro-border space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-astro-accent block">
                            3. Ashtakvarga Bindu Scores (SAV/BAV)
                          </span>
                          <span className="font-semibold text-astro-text">{domain.proPillars.ashtakvargaPoints}</span>
                        </div>

                        <div className="p-3 rounded-xl bg-astro-card border border-astro-border space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-astro-accent block">
                            4. Navamsha (D9) Harmonic Confirmation
                          </span>
                          <span className="font-semibold text-astro-primary">{domain.proPillars.navamshaConfirmation}</span>
                        </div>
                      </div>

                      {/* Probable Peak Date Calculation Engine Box */}
                      <div className="p-3 rounded-xl bg-astro-teal-tint border border-astro-primary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs font-mono">
                        <div>
                          <span className="font-bold text-astro-primary">{isHi ? 'गणित विधि:' : 'Confluence Algorithm:'} </span>
                          <span className="text-astro-secondary-text">
                            Dasha PD ({pdName}) + Transit Sun/Jupiter Confluence
                          </span>
                        </div>
                        <div className="font-mono font-bold text-astro-primary bg-astro-card px-3 py-1 rounded border border-astro-border shadow-2xs">
                          Peak Energy Window: {domain.peakWindow}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* SECTION 4: 13 CLASSICAL LIFE THEMES EXACT EVENT DATE PREDICTOR ENGINE */}
      <div className="pt-4 border-t border-astro-border">
        <LifeEventDatePredictor kundali={kundali} lang={lang} />
      </div>
    </div>
  );
};
